<template>
  <b-row class="mt-4 product-tile" no-gutters align-v="center" align-h="center">
    <b-col cols="4" class="m-auto text-center pb-4">
      <b-img
        v-if="localValue.image"
        class="product-image shadow"
        :alt="localValue.title"
        :src="localValue.image"
        :title="localValue.title"
        fluid
        lazy
        width="160"
        height="160"
        @click="viewing = localValue"
        @error="handleImageError"
      />
      <div v-else class="image-placeholder product-placeholder">
        <div>{{ imagePlaceholder(localValue.title) }}</div>
      </div>
    </b-col>

    <b-col cols="12" class="product-tile__title">
      {{ localValue.title }}
    </b-col>

    <b-col v-show="showActions" cols="12">
      <div class="my-3">
        <product-modifiers
          :value="localValue.modifiers"
          :validated.sync="modifiersValid"
          @input="updateModifiers"
        />
      </div>

      <b-row v-if="isRegular" align-v="center" align-h="center" no-gutters>
        <b-col cols="12" class="pb-2">
          <b-button-group class="cart-products-btn w-100">
            <b-button
              class="btn col-4"
              @click="removeFromCart(localValue)"
            >
              Remove
            </b-button>
            <b-button
              v-if="!localValue.max_weekly_frequency && !localValue.subscription"
              class="btn-outline"
              @click="subtractProductQty(localValue)"
            >
              <b-icon-dash />
            </b-button>
            <b-button
              v-if="!localValue.max_weekly_frequency && !localValue.subscription"
              class="btn-outline"
              @click="addProductQty(localValue)"
            >
              <b-icon-plus />
            </b-button>
          </b-button-group>
        </b-col>
      </b-row>
      

      <b-row
        align-v="center"
        align-h="center"
        no-gutters
        class="justify-content-between pt-1"
      >
        <b-col cols="auto">
          <span v-if="isRegular">{{ localValue.quantityOrdered }} x
            <Currency
              primary-font-size="1em"
              secondary-font-size=".8em"
              prepend=""
              :currency="unitPrice"
            />
          </span>
        </b-col>
        <b-col cols="auto" class="font-weight-bold">
          <Currency
            primary-font-size="1rem"
            secondary-font-size=".8em"
            prepend="$"
            :currency="completePrice"
          />
        </b-col>
      </b-row>
    </b-col>

    <product-info v-if="viewing" v-model="viewing" :view-reviews="reviewing" />
    <product-reviews v-if="reviewing" v-model="reviewing" />
  </b-row>
</template>

<script>
import Currency from '@/Currency.vue';
import ProductInfo from '@/shop/product-partials/ProductInfo.vue';
import ProductModifiers from '@/shop/product-partials/ProductModifiers.vue';
import ProductReviews from '@/shop/product-partials/ProductReviews.vue';
import { mapActions, mapGetters } from 'vuex';
import {
  addProductQty,
  addToCart,
  imagePlaceholder,
  removeFromCart,
  subtractProductQty,
} from '^/utilities';

export default {
  name: 'CartProduct',
  components: {
    ProductInfo,
    ProductReviews,
    ProductModifiers,
    Currency,
  },
  props: {
    value: { 
      type: Object, 
      required: true 
    }, 
    showActions: { 
      type: Boolean, 
      default: true 
    }
  },
  data() {
    return {
      viewing: null,
      reviewing: null,
      modifiersValid: null,
      localValue: { ...this.value }
    };
  },
  watch: {
    value: {
      handler(newVal) {
        this.localValue = { ...newVal };
      },
      deep: true,
      immediate: true
    },
    modifiersValid(val) {
      this.$store.commit(
        'toggleModifierValidation',
        val.findIndex((x) => !x) === -1
      );
    },
  },
  created() {
    this.$watch(
      (vm) => [
        vm.localValue.quantityOrdered,
        vm.localValue.orderedWeight,
        vm.localValue.modifiers,
        vm.cartInteractions,
      ],
      function () {
        this.updateQuote();
      },
      { deep: true, immediate: true }
    );
  },
  computed: {
    ...mapGetters(['cartInteractions', 'region']),
    isRegular() {
      return true;
    },
    outOfStock() {
      return false;
    },
    stock() {
      return null;
    },
    unitPrice() {
      return this.localValue.price;
    },
    completePrice() {
      return this.localValue.price_with_defaults;
    },
  },
  methods: {
    ...mapActions(['updateQuote']),
    addToCart,
    removeFromCart,
    subtractProductQty(product) {
      const updatedProduct = subtractProductQty(product);
      this.updateLocalValue(updatedProduct);
      this.$store.commit('updateProductInCart', updatedProduct);
    },
    addProductQty(product) {
      const updatedProduct = addProductQty(product);
      this.updateLocalValue(updatedProduct);
      this.$store.commit('updateProductInCart', updatedProduct);
    },
    imagePlaceholder,
    handleImageError() {
      this.localValue = { ...this.localValue, image: null };
    },
    updateLocalValue(newValue) {
      this.localValue = { ...newValue };
      this.$emit('input', this.localValue);
    },
    updateModifiers(modifiers) {
      this.localValue = { 
        ...this.localValue, 
        modifiers 
      };
      this.$emit('input', this.localValue);
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../../styles/variables.module.scss';

.cart-products-btn {
  @include buttons;

  .btn {
    box-shadow: none !important;
    font-size: 110%;
  }

  & {
    justify-content: center;
  }
}

@media #{$smAndAbove} {
  .product-right-column {
    margin-left: 15px;
  }
}

@media #{$xs} {
  .product-right-column {
    margin-left: 0;
    margin-top: 15px;
  }
}

.w-100 {
  width: 100%;
}

.cart-products-btn {
  button {
    border-radius: 0 !important;
  }
}

.product-placeholder {
  width: 160px;
  min-height: 160px;
}

.product-tile {
  border-radius: 5px;

  &__title {
    font-weight: bold;
    font-size: 18px;
    text-align: center;
  }

  .product-title {
    color: black;
  }

  .product-qty-input {
    font-size: 0.85em;
    width: 70px;
    height: 38px !important;
  }

  .product-img-wrapper {
    position: relative;

    img {
      max-width: 120px;
    }

    .badge {
      padding-top: 5px;
      border-top-left-radius: 0;
      border-top-right-radius: 0;
      font-size: 0.85em;
      letter-spacing: 0.085em;
      font-family: $staatliches-font;
      position: absolute;
      bottom: 0;
      width: 100%;
      max-width: 170px;
      text-align: center;
      background-color: white;
      border: 1px solid #dee2e6;
      border-top: none;
      color: var(--primary);
    }
  }
}

.tag-space {
  margin-right: 2px;
}

div > .tag-space:last-child {
  margin-right: 0;
}

.sub-info {
  border-radius: 4px;
  font-size: 1em;
  color: #555;
  font-weight: bold;
}

.product-image {
  border: 1px solid var(--primary);
  border-radius: 5px;
}
</style>
